import { FC, useEffect, useState } from 'react';

// Externals
import firebase from 'firebase';
import { Formik, FormikHelpers, FormikState } from 'formik';
import _ from 'lodash';
import { useSnackbar } from 'notistack';
import { useFirestoreDocData, useFirestore } from 'reactfire';
import * as yup from 'yup';

// Material
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  LinearProgress,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core';

// Material Pickers
import { DatePicker } from '@material-ui/pickers';

// Models
import { MeasurementDTO } from 'app/models/measurement.dto';

// Styles
const useStyles = makeStyles((theme) => ({
  content: {
    paddingTop: '0 !important'
  },
  title: {
    padding: theme.spacing(2, 3, 1)
  },
  linearProgress: {
    marginBottom: theme.spacing(1.5)
  }
}));

interface Props {
  stationId: string;
  id: string;
  open: boolean;
  onClose: () => void;
}

interface States {
  date: Date;
  value: number;
  startDate: Date;
  endDate: Date;
}

const EditModal: FC<Props> = ({ stationId, id, open, onClose }) => {
  const classes = useStyles();

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const measurementRef = useFirestore().collection('stations2023').doc(stationId).collection('data').doc(id);

  const { status, data, hasEmitted } = useFirestoreDocData<MeasurementDTO>(measurementRef, {
    initialData: {
      date: firebase.firestore.Timestamp.fromDate(new Date()),
      startDate: firebase.firestore.Timestamp.fromDate(new Date()),
      endDate: firebase.firestore.Timestamp.fromDate(new Date()),
      value: 0
    }
  });

  const [initialValues, setInitialValues] = useState<States>({
    date: new Date(),
    value: 0,
    startDate: new Date(),
    endDate: new Date()
  });

  const onReset = (resetForm: (nextState?: Partial<FormikState<States>> | undefined) => void) => {
    resetForm();

    onClose();
  };

  const onSubmit = async (values: States, actions: FormikHelpers<States>) => {
    try {
      const measurement: MeasurementDTO = {
        id: id,
        date: firebase.firestore.Timestamp.fromDate(new Date(values.date)),
        startDate: firebase.firestore.Timestamp.fromDate(new Date(values.startDate)),
        endDate: firebase.firestore.Timestamp.fromDate(new Date(values.endDate)),
        value: values.value
      };

      await measurementRef.set(measurement, { merge: true });

      const key = enqueueSnackbar(`La mesure en dioxyde d'azote a été modifiée avec succès !`, {
        variant: 'success',
        onClick: () => {
          closeSnackbar(key);
        }
      });

      onReset(actions.resetForm);
    } catch (error) {
      console.error(error);

      const key = enqueueSnackbar(
        `La mesure en dioxyde d'azote n'a pu être modifiée. Si le problème persiste, veuillez contacter votre support technique.`,
        {
          variant: 'error',
          onClick: () => {
            closeSnackbar(key);
          }
        }
      );
    }
  };

  useEffect(() => {
    if (!_.isEmpty(data))
      setInitialValues({
        date: data.date.toDate(),
        value: data.value,
        startDate: data.startDate.toDate(),
        endDate: data.endDate.toDate()
      });
  }, [data]);

  return (
    <Formik
      initialValues={initialValues}
      isInitialValid={false}
      enableReinitialize={true}
      validateOnMount={true}
      validationSchema={yup.object().shape({
        date: yup.date().required(`Le date est nécessaire.`),
        value: yup
          .number()
          .min(0, `La valeur ne peut pas être inférieur à 0 µg/m3.`)
          .required(`La valeur est nécessaire.`)
      })}
      onSubmit={onSubmit}
    >
      {({
        handleBlur,
        handleChange,
        handleSubmit,
        resetForm,
        setFieldValue,
        errors,
        touched,
        values,
        isSubmitting,
        isValid
      }) => (
        <Dialog open={open} onClose={() => onReset(resetForm)} maxWidth="xs">
          <DialogTitle className={classes.title} disableTypography>
            <Typography variant="h3">Modification</Typography>
          </DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent className={classes.content}>
              <DialogContentText>
                {status === 'loading' || !hasEmitted
                  ? 'Chargement...'
                  : 'Veuillez remplir les champs ci-dessous. Les champs marqués par un astérisque sont obligatoires.'}
              </DialogContentText>
              {(status === 'loading' || !hasEmitted || isSubmitting) && (
                <LinearProgress className={classes.linearProgress} color="secondary" />
              )}
              <DatePicker
                autoOk={true}
                disabled={status === 'loading' || !hasEmitted || isSubmitting}
                error={Boolean(touched.date && errors.date)}
                fullWidth={true}
                helperText={touched.date && errors.date}
                label="Date*"
                margin="dense"
                name="date"
                onBlur={handleBlur}
                onChange={(date) => {
                  if (date) setFieldValue('date', date.toDate());
                }}
                value={values.date}
                inputVariant="outlined"
                views={['year', 'month']}
                openTo="month"
                variant="dialog"
              />
              <TextField
                disabled={status === 'loading' || !hasEmitted || isSubmitting}
                error={Boolean(touched.value && errors.value)}
                fullWidth={true}
                helperText={touched.value && errors.value}
                label="Valeur*"
                margin="dense"
                name="value"
                onBlur={handleBlur}
                onChange={handleChange}
                type="number"
                value={values.value}
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography variant="inherit">µg/m3</Typography>
                    </InputAdornment>
                  )
                }}
              />
              <DatePicker
                autoOk={true}
                disableToolbar={true}
                disabled={status === 'loading' || !hasEmitted || isSubmitting}
                error={Boolean(touched.startDate && errors.startDate)}
                fullWidth={true}
                helperText={touched.startDate && errors.startDate}
                label="Date de début*"
                margin="dense"
                name="startDate"
                onBlur={handleBlur}
                onChange={(date) => {
                  if (date) setFieldValue('startDate', date.toDate());
                }}
                value={values.startDate}
                inputVariant="outlined"
                views={['date']}
                format="L"
                variant="inline"
              />
              <DatePicker
                autoOk={true}
                disableToolbar={true}
                disabled={status === 'loading' || !hasEmitted || isSubmitting}
                error={Boolean(touched.endDate && errors.endDate)}
                fullWidth={true}
                helperText={touched.endDate && errors.endDate}
                label="Date de fin*"
                margin="dense"
                name="endDate"
                onBlur={handleBlur}
                onChange={(date) => {
                  if (date) setFieldValue('endDate', date.toDate());
                }}
                value={values.endDate}
                inputVariant="outlined"
                views={['date']}
                format="L"
                variant="inline"
              />
            </DialogContent>
            <DialogActions>
              <Button color="primary" onClick={() => onReset(resetForm)} type="reset">
                Annuler
              </Button>
              <Button
                color="primary"
                disabled={status === 'loading' || !hasEmitted || isSubmitting || !isValid}
                type="submit"
                variant="contained"
              >
                Modifer
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      )}
    </Formik>
  );
};

export default EditModal;
